import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ChevronRight } from 'lucide-react';
import { useLiveIpoFeed } from '../../hooks/useLiveIpoFeed';

const STATUS_BADGE = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
};

const TickerItem = ({ item }) => {
  const content = (
    <>
      <span className="font-semibold text-slate-800">{item.name}</span>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase ${STATUS_BADGE[item.statusType]}`}>{item.status}</span>
      {item.gmp && (
        <span className={`font-semibold flex items-center gap-0.5 ${item.gmpPercent < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
          <TrendingUp className="w-3 h-3 inline" />
          <span>GMP: {item.gmp}</span>
        </span>
      )}
      {item.subscription && (
        <span className="text-slate-500 font-medium">
          Sub: <strong className="text-slate-700">{item.subscription}</strong>
        </span>
      )}
      {!item.subscription && item.price && (
        <span className="text-slate-500 font-medium">
          Price: <strong className="text-slate-700">{item.price}</strong>
        </span>
      )}
      <span className="text-slate-300 ml-2">•</span>
    </>
  );
  const className = 'inline-flex items-center gap-2 hover:bg-white/80 px-2.5 py-0.5 rounded transition-colors';
  return item.link ? (
    <Link to={item.link} className={className}>
      {content}
    </Link>
  ) : (
    <span className={className}>{content}</span>
  );
};

// Site-wide strip of live IPO quotes (GMP board + subscription feed)
export const TickerTape = () => {
  const { items, loading } = useLiveIpoFeed();

  return (
    <div className="w-full bg-slate-50/90 border-b border-slate-200/80 overflow-hidden text-xs select-none mb-3 sm:mb-5">
      <div className="w-full flex items-center">
        {/* Left 'LIVE' Indicator Pill */}
        <div className="flex-shrink-0 flex items-center gap-1.5 bg-gradient-to-r from-[#008080] to-[#006666] text-white font-bold px-3 py-1.5 shadow-sm z-10">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-300"></span>
          </span>
          <span className="tracking-wider uppercase text-[11px]">LIVE</span>
        </div>

        {/* Scrolling Ticker Strip */}
        <div className="flex-1 overflow-hidden relative group">
          {items.length ? (
            <div className="flex items-center space-x-6 whitespace-nowrap animate-marquee group-hover:[animation-play-state:paused] py-1.5 px-4">
              {/* Double the list for continuous infinite loop */}
              {[...items, ...items].map((item, idx) => (
                <TickerItem key={`${item.id}-${idx}`} item={item} />
              ))}
            </div>
          ) : (
            <p className="py-1.5 px-4 text-slate-500 font-medium truncate">
              {loading ? 'Loading live IPO quotes…' : 'No IPOs are open or scheduled right now.'}
            </p>
          )}
        </div>

        {/* Right shortcut */}
        <div className="hidden md:flex items-center flex-shrink-0 px-3 py-1 text-slate-500 hover:text-indigo-600 transition-colors z-10 bg-slate-50 border-l border-slate-200">
          <Link to="/ipo-gmp" className="flex items-center gap-1 font-medium text-[11px]">
            <span>Market Feed</span>
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TickerTape;
